import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import {
  CatalogService,
  CustomerDepartmentAddFavoriteQualificationsRequest,
  CustomerDepartmentCreateRequest,
  CustomerDepartmentQualificationCreateRequest,
  CustomerDepartmentResponse,
  CustomerDepartmentService,
  CustomerDepartmentUpdateRequest,
  CustomerDepartmentUpdateShiftTimesRequest,
  CustomerLocationDepartmentRequirementService,
  CustomerLocationDepartmentService,
  CustomerService,
  DepartmentListItemResponse,
  DutyModelWithAdoptionListItemResponse,
  EmptySuccessfulResponse,
  ImportUploadResponse,
  RequirementCategoryResponse,
  UpdateUsersRequest,
  UserItemResponse,
  GeneralApiResponse,
  CustomerDepartmentUpdateRequirementsRequest,
} from '../../../api';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { baseDeepClone } from '../helpers/base-deep-clone';
import { BaseApplicationTagType } from '../enums/base-application.enum';
import { BASE_SKIP_ERROR_TOAST } from '../../core/constants/base-http-context-tokens';

@Injectable({ providedIn: 'root' })
export class BaseCustomerDepartmentService {
  constructor(
    private readonly customerDepartmentService: CustomerDepartmentService,
    private readonly customerLocationDepartmentService: CustomerLocationDepartmentService,
    private readonly customerLocationDepartmentRequirementsService: CustomerLocationDepartmentRequirementService,
    private readonly customerService: CustomerService,
    private readonly catalogService: CatalogService
  ) {}

  getCustomerDepartmentList(customerId: number | string): Observable<DepartmentListItemResponse[]> {
    return this.customerDepartmentService
      .getCustomerDepartmentListOfDepartments({ customer: customerId as number })
      .pipe(
        map((response): DepartmentListItemResponse[] =>
          response.data?.length ? (response.data as DepartmentListItemResponse[]) : []
        )
      );
  }

  getCustomerDepartment(params: { department: number; customer: number }): Observable<
    GeneralApiResponse & {
      data?: CustomerDepartmentResponse;
    }
  > {
    return this.customerDepartmentService.getCustomerDepartmentShow(params);
  }

  getCustomerUsers(params: {
    customer: number;
    search?: string;
    active?: boolean;
    roles?: Array<'user' | 'admin' | 'workCouncil'>;
  }): Observable<UserItemResponse[]> {
    return this.customerService
      .getCustomersUsersList({
        customer: params.customer,
        'role[]': params.roles,
        active: params.active,
        search: params.search,
      })
      .pipe(map((response) => (response.data?.length ? response.data : [])));
  }

  updateUsersToCustomerDepartment(params: {
    department: number;
    customer: number;
    location: number;
    body: UpdateUsersRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.putLocationDepartmentUpdateUsers(params);
  }

  updateMasterData(params: {
    department: number;
    customer: number;
    location: number;
    body: CustomerDepartmentUpdateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.putCustomerLocationDepartmentUpdate(params);
  }

  /** Every duty model of the department's location, flagged with whether this department adopted it. */
  getDutyModels(params: {
    customer: number;
    location: number;
    department: number;
  }): Observable<DutyModelWithAdoptionListItemResponse[]> {
    return this.customerLocationDepartmentService
      .getCustomerLocationDepartmentDutyModelsList(params)
      .pipe(map((response): DutyModelWithAdoptionListItemResponse[] => response.data || []));
  }

  /** Adoption is POST / DELETE on one `.../adoption` resource; `adopted` is the state to move to. */
  setDutyModelAdoption(params: {
    customer: number;
    location: number;
    department: number;
    dutyModel: number;
    adopted: boolean;
  }): Observable<unknown> {
    const { adopted, ...pathParams } = params;

    return adopted
      ? this.customerLocationDepartmentService.postCustomerLocationDepartmentDutyModelsAdopt(pathParams)
      : this.customerLocationDepartmentService.deleteCustomerLocationDepartmentDutyModelsUnadopt(pathParams);
  }

  updateShiftTimes(params: {
    department: number;
    customer: number;
    location: number;
    body: CustomerDepartmentUpdateShiftTimesRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.putCustomerLocationDepartmentTimeShifts(params);
  }

  getAvailableRequirementCategories(tag?: BaseApplicationTagType): Observable<RequirementCategoryResponse[]> {
    return this.catalogService
      .getAppApiWebV1CatalogsRequirementListIndex({ tag })
      .pipe(map((response) => response.data.categories));
  }

  updateRequirements(params: {
    customer: number;
    location: number;
    department: number;
    body: CustomerDepartmentUpdateRequirementsRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentRequirementsService.putCustomerLocationDepartmentRequirementUpdate(params);
  }

  deleteRequirement(params: {
    department: number;
    requirement: number;
    location: number;
    customer: number;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentRequirementsService.deleteCustomerLocationDepartmentRequirementDelete(params);
  }

  createDepartment(
    customerId: number,
    location: number,
    body: CustomerDepartmentCreateRequest
  ): Observable<DepartmentListItemResponse> {
    if (body.primaryData.name) {
      return this.customerLocationDepartmentService
        .postCustomerDepartmentCreate({
          customer: customerId,
          location: location,
          body,
        })
        .pipe(map((response) => response.data));
    }

    return this.customerService.getCustomerGet({ customer: customerId }).pipe(
      mergeMap((result) => {
        const requestBody = baseDeepClone(body) as CustomerDepartmentCreateRequest;
        requestBody.primaryData.name = !!result.data.primaryData?.name ? result.data.primaryData?.name : '';

        return this.customerLocationDepartmentService
          .postCustomerDepartmentCreate({
            customer: customerId,
            location: location,
            body: requestBody,
          })
          .pipe(map((response) => response.data));
      })
    );
  }

  addFavouriteQualifications(params: {
    department: number;
    customer: number;
    location: number;
    body: CustomerDepartmentAddFavoriteQualificationsRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.postCustomerLocationDepartmentQualificationsFavoriteAdd(params);
  }

  deleteFavouriteQualification(params: {
    department: number;
    qualification: number;
    customer: number;
    location: number;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.deleteCustomerLocationDepartmentQualificationsFavoriteDelete(params);
  }

  updateDepartmentQualificationDescription(params: {
    qualification: number;
    customer: number;
    department: number;
    location: number;
    body: CustomerDepartmentQualificationCreateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerLocationDepartmentService.postCustomerLocationDepartmentQualificationsDescriptionCreate(params);
  }

  getDepartmentImportTemplate(customer: number): Observable<Blob> {
    return this.customerDepartmentService.getCustomerDepartmentImportTemplate({ customer });
  }

  uploadDepartmentImport(customer: number, file: File): Observable<ImportUploadResponse | undefined> {
    return this.customerDepartmentService
      .postCustomerDepartmentImportUpload({
        customer,
        body: { file },
        context: new HttpContext().set(BASE_SKIP_ERROR_TOAST, true),
      })
      .pipe(map((response) => response.data));
  }

  processDepartmentImport(customer: number, importId: string): Observable<void> {
    return this.customerDepartmentService.postCustomerDepartmentImportProcess({ customer, import: importId });
  }
}
